import { useEffect, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  GUARD_APP_CLOSE_REASON_COPY,
  phoneAppChipLines,
  requestGuardAppAccess,
  updateGuardAppAccessRequest,
  validateGuardAppAccessDraft,
  type GuardAppAccessRequestRow,
  type GuardAppPlatform,
} from "../data/guard-app-access";
import { humanizeLabel } from "../data/alert-queue";
import { resolveGuardName } from "../lib/guard-name";
import type { MemberRow } from "../data/members";

// Request Guard app access - the tenant drawer (design lock 2026-08-09, mobile-app-access mock).
//
// One form that asks for the thing the product has never held: the store account signed in on
// the officer's phone. It is not a second login, and this surface says so twice - once beside
// the identity block and again on the receipt. The two identities are kept visually apart on
// purpose: work login in the read-only person block, store account in its own bordered panel,
// machine data in mono.
//
// Fail closed: Both with one address missing cannot submit, and the blocker says which one.
// The receipt promises an invite from Apple or Google, never an install and never a login.
// Editing is allowed only while the request is still Requested (decision lock 4); after a
// store mark the founder owns the address and the drawer goes read-only.

const PLATFORM_OPTIONS: ReadonlyArray<{ value: GuardAppPlatform; label: string }> = [
  { value: "ios", label: "iPhone" },
  { value: "android", label: "Android" },
  { value: "both", label: "Both" },
];

export function GuardAppAccessDrawer({
  client,
  accountId,
  member,
  existing,
  onClose,
  onSaved,
}: {
  client: SupabaseClient;
  accountId: string;
  member: MemberRow;
  /** The member's relevant request: open one first, else latest closed, else null. */
  existing: GuardAppAccessRequestRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const editable = existing === null || existing.status === "closed" || existing.status === "requested";
  const editingOpenRequest = existing !== null && existing.status === "requested";

  const [platform, setPlatform] = useState<GuardAppPlatform>(
    editingOpenRequest ? existing.platform : "ios",
  );
  const [appleEmail, setAppleEmail] = useState(editingOpenRequest ? (existing.appleStoreEmail ?? "") : "");
  const [googleEmail, setGoogleEmail] = useState(
    editingOpenRequest ? (existing.googleStoreEmail ?? "") : "",
  );
  const [note, setNote] = useState(editingOpenRequest ? (existing.note ?? "") : "");
  const [blocker, setBlocker] = useState<string | null>(null);
  const [badField, setBadField] = useState<"apple" | "google" | "note" | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<{ platform: GuardAppPlatform; apple: string | null; google: string | null } | null>(
    null,
  );

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const resolved = resolveGuardName({
    displayName: member.memberDisplayName,
    label: member.memberLabel,
    ref: member.memberRef,
    fallback: "Member",
  });
  const initials = resolved.name
    .split(/\s+/)
    .map((part) => part.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();

  function clearBlocker(): void {
    setBlocker(null);
    setBadField(null);
  }

  async function submit(): Promise<void> {
    if (busy || !editable) return;
    const validation = validateGuardAppAccessDraft({
      platform,
      appleStoreEmail: appleEmail,
      googleStoreEmail: googleEmail,
      note,
    });
    if (!validation.ok) {
      setBlocker(validation.reason);
      setBadField(validation.field);
      return;
    }
    setBusy(true);
    clearBlocker();
    const payload = {
      platform,
      appleStoreEmail: validation.appleStoreEmail,
      googleStoreEmail: validation.googleStoreEmail,
      note: validation.note,
    };
    const outcome = editingOpenRequest
      ? await updateGuardAppAccessRequest(client, accountId, existing.requestId, payload)
      : await requestGuardAppAccess(client, accountId, member.membershipId, payload);
    setBusy(false);
    if (!outcome.ok) {
      setBlocker(outcome.reason);
      return;
    }
    setSent({ platform, apple: payload.appleStoreEmail, google: payload.googleStoreEmail });
    onSaved();
  }

  const showApple = platform === "ios" || platform === "both";
  const showGoogle = platform === "android" || platform === "both";

  return (
    <dialog
      ref={dialogRef}
      className="gaa-drawer"
      aria-label="Request Guard app access"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === dialogRef.current) onClose();
      }}
    >
      <div className="gaa-drawer__frame">
        <header className="gaa-drawer__head">
          <p className="gaa-drawer__eyebrow">SecOps Guard</p>
          <h2 className="gaa-drawer__title">
            {sent !== null ? "Request sent." : editingOpenRequest ? "Update app access request" : "Request app access"}
          </h2>
          {sent === null ? (
            <p className="gaa-drawer__sub">
              The Guard app is invite only. Send us the store account on the officer's phone and we
              will add it to private testing. iOS TestFlight and Android internal testing.
            </p>
          ) : null}
          <button type="button" className="gaa-drawer__close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </header>

        {sent !== null ? (
          <ReceiptBody name={resolved.name} sent={sent} onDone={onClose} />
        ) : (
          <>
            <div className="gaa-drawer__body">
              <section className="gaa-block">
                <h3 className="gaa-block__label">Who this is for</h3>
                <div className="gaa-who">
                  <span className="gaa-who__avatar" aria-hidden="true">
                    {initials}
                  </span>
                  <span>
                    <span className="gaa-who__name">{resolved.name}</span>
                    <span className="gaa-who__meta">
                      {humanizeLabel(member.role)}
                      {member.memberEmail !== null ? (
                        <>
                          {" · "}
                          <span className="gaa-mono">{member.memberEmail}</span>
                        </>
                      ) : null}
                    </span>
                  </span>
                </div>
                <p className="gaa-who__note">
                  This is their SecOps login. Requesting app access does not change it.
                </p>
                {existing !== null && existing.status === "closed" ? (
                  <p className="gaa-who__note">
                    Their previous request was closed
                    {existing.closedReason !== null
                      ? `: ${GUARD_APP_CLOSE_REASON_COPY[existing.closedReason]}.`
                      : "."}{" "}
                    This sends a fresh one.
                  </p>
                ) : null}
              </section>

              {editable ? (
                <>
                  <section className="gaa-block">
                    <h3 className="gaa-block__label">Which phone do they use on shift</h3>
                    <div className="gaa-seg" role="radiogroup" aria-label="Phone platform">
                      {PLATFORM_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          role="radio"
                          aria-checked={platform === option.value}
                          disabled={busy}
                          onClick={() => {
                            setPlatform(option.value);
                            clearBlocker();
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <p className="gaa-seg__help">Pick Both only if this person carries two phones on duty.</p>
                  </section>

                  <section className="gaa-block">
                    <div className="gaa-store">
                      <h3 className="gaa-store__head">Store account, not their SecOps login</h3>
                      <p className="gaa-store__why">
                        Apple and Google send the testing invite to the account signed in on the
                        phone. That address is often personal, so we have to ask rather than guess.
                      </p>

                      {showApple ? (
                        <div className={`gaa-field${badField === "apple" ? " gaa-field--bad" : ""}`}>
                          <div className="gaa-field__row">
                            <label htmlFor="gaa-apple-email">Apple ID email</label>
                            <span className="gaa-field__plat">TestFlight</span>
                          </div>
                          <input
                            id="gaa-apple-email"
                            type="text"
                            value={appleEmail}
                            placeholder="name@icloud.com"
                            disabled={busy}
                            onChange={(event) => {
                              setAppleEmail(event.target.value);
                              clearBlocker();
                            }}
                          />
                          <p className="gaa-field__help">
                            The Apple ID signed in on that iPhone. It may not be their work email. To
                            find it: Settings, then their name at the top.
                          </p>
                        </div>
                      ) : null}

                      {showGoogle ? (
                        <div className={`gaa-field${badField === "google" ? " gaa-field--bad" : ""}`}>
                          <div className="gaa-field__row">
                            <label htmlFor="gaa-google-email">Google account email</label>
                            <span className="gaa-field__plat">Play internal testing</span>
                          </div>
                          <input
                            id="gaa-google-email"
                            type="text"
                            value={googleEmail}
                            placeholder="name@gmail.com"
                            disabled={busy}
                            onChange={(event) => {
                              setGoogleEmail(event.target.value);
                              clearBlocker();
                            }}
                          />
                          <p className="gaa-field__help">
                            The Google account signed in to the Play Store on that phone. To find it:
                            Play Store, profile icon, the address at the top.
                          </p>
                        </div>
                      ) : null}

                      {platform === "both" ? (
                        <p className="gaa-store__both-note">
                          Two phones means two invites. Apple emails the Apple ID, Google emails the
                          Google account, and each one has to be accepted on its own phone.
                        </p>
                      ) : null}
                    </div>
                  </section>

                  <section className="gaa-block">
                    <h3 className="gaa-block__label">
                      Anything we should know <span className="gaa-block__optional">(optional)</span>
                    </h3>
                    <textarea
                      className={`gaa-note${badField === "note" ? " gaa-note--bad" : ""}`}
                      aria-label="Anything we should know"
                      value={note}
                      disabled={busy}
                      placeholder="Example: starts Monday nights, would like it before the first shift."
                      onChange={(event) => {
                        setNote(event.target.value);
                        clearBlocker();
                      }}
                    />
                  </section>
                </>
              ) : (
                <OpenRequestSummary request={existing} />
              )}
            </div>

            {editable ? (
              <footer className="gaa-drawer__foot">
                <p className="gaa-drawer__expect">
                  Submitting sends this to the SecOps team. Apple or Google emails the officer
                  directly once the account is added, so the invite will not come from us.
                </p>
                <div className="gaa-drawer__actions">
                  <button
                    type="button"
                    className="minter__confirm-go gaa-drawer__submit"
                    disabled={busy}
                    onClick={() => void submit()}
                  >
                    {busy ? "Sending..." : editingOpenRequest ? "Update request" : "Submit request"}
                  </button>
                  <button type="button" className="builder__navbtn" disabled={busy} onClick={onClose}>
                    Cancel
                  </button>
                </div>
                {blocker !== null ? (
                  <p className="gaa-drawer__blocker" role="alert">
                    {blocker}
                  </p>
                ) : null}
              </footer>
            ) : (
              <footer className="gaa-drawer__foot">
                <div className="gaa-drawer__actions">
                  <button type="button" className="builder__navbtn" onClick={onClose}>
                    Close
                  </button>
                </div>
              </footer>
            )}
          </>
        )}
      </div>
    </dialog>
  );
}

/**
 * Read-only view once the founder has marked any store: the address is already sitting in a
 * store console, so only the founder can change it now (decision lock 4). The copy sets the
 * wait honestly: the invite comes from Apple or Google, not from SecOps.
 */
function OpenRequestSummary({ request }: { request: GuardAppAccessRequestRow }) {
  const chips = phoneAppChipLines(request);
  return (
    <section className="gaa-block">
      <h3 className="gaa-block__label">Where this stands</h3>
      <div className="gaa-summary__chips">
        {chips.map((chip) => (
          <span key={chip.label} className={`gaa-chip gaa-chip--${chip.tone}`}>
            {chip.label}
          </span>
        ))}
      </div>
      <dl className="gaa-summary">
        {request.appleStoreEmail !== null ? (
          <div>
            <dt>Apple ID</dt>
            <dd className="gaa-mono">{request.appleStoreEmail}</dd>
          </div>
        ) : null}
        {request.googleStoreEmail !== null ? (
          <div>
            <dt>Google account</dt>
            <dd className="gaa-mono">{request.googleStoreEmail}</dd>
          </div>
        ) : null}
        {request.note !== null ? (
          <div>
            <dt>Note</dt>
            <dd>{request.note}</dd>
          </div>
        ) : null}
      </dl>
      <p className="gaa-who__note">
        {request.installed
          ? "The phone checked in running Guard. Nothing left to do."
          : "The store account is with the SecOps team. Apple or Google emails the officer directly, and the invite can land in a personal inbox. The address can only be corrected by SecOps now that it is in a store console."}
      </p>
    </section>
  );
}

/** The receipt: a recorded fact, then three honest steps that promise an invite, not an install. */
function ReceiptBody({
  name,
  sent,
  onDone,
}: {
  name: string;
  sent: { platform: GuardAppPlatform; apple: string | null; google: string | null };
  onDone: () => void;
}) {
  const stores =
    sent.platform === "both" ? "Apple and Google each email" : sent.platform === "ios" ? "Apple emails" : "Google emails";
  return (
    <div className="gaa-drawer__body gaa-receipt">
      <span className="gaa-receipt__check" aria-hidden="true">
        ✓
      </span>
      <p className="gaa-receipt__lede">
        We have the store account for {name}. Here is what happens next, and roughly when.
      </p>
      <ol className="gaa-receipt__steps">
        <li>
          <strong>We add the account to private testing.</strong> Usually the same working day.{" "}
          {sent.apple !== null ? <span className="gaa-mono">{sent.apple}</span> : null}
          {sent.apple !== null && sent.google !== null ? " " : null}
          {sent.google !== null ? <span className="gaa-mono">{sent.google}</span> : null}
        </li>
        <li>
          <strong>{stores} that address a testing invite.</strong> The email comes from the store,
          not from SecOps, and it can land in a personal inbox.
        </li>
        <li>
          <strong>They accept the invite and install SecOps Guard.</strong> The member row here
          changes to Installed once the phone checks in.
        </li>
      </ol>
      <p className="gaa-receipt__guard">
        <strong>This is not a login invite.</strong> {name} can already sign in to SecOps with their
        work email. This request only puts their phone on the list for the app.
      </p>
      <div className="gaa-drawer__actions">
        <button type="button" className="minter__confirm-go gaa-drawer__submit" onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  );
}
